// "Email finance" on the VALS Disbursements page: tells VALS Finance that a provider's
// invoice has been claimed in ATLAS and can be paid, with the provider's invoice and the
// ATLAS claim receipt attached. Sent from the service's Gmail account the same way the Fee
// Slips page's clerk emails are (src/lib/clerkEmail.js).
//
// An invoice ticked "VALS to be reimbursed" is not claimed in ATLAS: it is paid from the crim
// budget and VALS reimburses it, so its email says that instead (Emma's wording, 23 Oct 2026).

import { DEFAULT_SIGN_OFF, greetingFor, personName } from "./clerkEmail.js";
import { partialSplit } from "./partialPaid.js";

const hasFigure = (value) => value !== null && value !== undefined && value !== "";
const remainingFromBudget = (item) => partialSplit(item.amount, item.vlaClaimed, item.budgetRemaining).fromBudget ?? 0;

export const VALS_FINANCE_RECIPIENTS = [
  { address: "finance@vals.org.au", label: "finance@vals.org.au" },
  { address: "gnagal@vals.org.au", label: "gnagal@vals.org.au" },
];

// Deadline Guard takes the email as one JSON body through the portal's proxy, which cannot
// accept more than about 4.5 MB. Attachments travel as base64 (a third larger), so their
// files are kept under 3 MB together.
export const MAX_ATTACHMENT_BYTES = 3 * 1024 * 1024;

const money = (value) => (Number(value) || 0).toLocaleString("en-AU", { style: "currency", currency: "AUD" });
const day = (value) => {
  const [y, m, d] = String(value || "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "";
};

export const isCrimBudgetEmail = (item = {}) => item.reimburse === true;

export function financeEmailSubject(item = {}) {
  const client = personName(item.clientName);
  const provider = String(item.supplier || "").trim();
  return [
    isCrimBudgetEmail(item) ? "Crim Budget Payment" : "Disbursement claimed in ATLAS - ready to pay",
    client && `Matter of ${client}`,
    provider && `${provider}${item.invoiceRef ? ` invoice ${item.invoiceRef}` : ""}`,
  ].filter(Boolean).join(" - ");
}

export function financeEmailBody(item = {}, { date = new Date(), signOff = DEFAULT_SIGN_OFF, withReceipt = true, withInvoice = true } = {}) {
  const details = [
    personName(item.clientName) && `Client: ${personName(item.clientName)}`,
    item.supplier && `Provider: ${item.supplier}`,
    item.invoiceRef && `Invoice number: ${item.invoiceRef}`,
    day(item.invoiceDate) && `Invoice date: ${day(item.invoiceDate)}`,
    Number(item.amount) > 0 && `Amount: ${money(item.amount)}`,
    item.aidRef && `Aid / file number: ${item.aidRef}`,
    // Partial Paid: how the invoice is split between the VLA claim and the budget.
    hasFigure(item.vlaClaimed) && `Claimed from VLA: ${money(item.vlaClaimed)}`,
    hasFigure(item.vlaClaimed) && `Remaining to pay from budget: ${money(remainingFromBudget(item))}`,
  ].filter(Boolean);
  const attached = [withInvoice && "the provider's invoice", withReceipt && "the ATLAS claim receipt"].filter(Boolean);
  return [
    `${greetingFor(date)},`,
    "",
    isCrimBudgetEmail(item)
      ? "The invoice attached is to be paid from the crim budget and VALS to be reimbursed."
      : "The invoice below has been claimed in ATLAS and can now be paid to the provider.",
    "",
    ...details,
    ...(attached.length ? ["", `Please find attached ${attached.join(" and ")}.`] : []),
    "",
    "Kind regards,",
    String(signOff || DEFAULT_SIGN_OFF).trim(),
  ].join("\n");
}

// What is recorded on the disbursement once the email has gone: the new status (unless it is
// already paid), and a dated note naming who it went to.
export function afterFinanceEmailed(item = {}, { to = [], cc = "", at = new Date().toISOString(), by = "", receipt = null } = {}) {
  const crim = isCrimBudgetEmail(item);
  const copied = String(cc || "").trim();
  const line = `Request sent to Finance to pay invoice ${day(at)} to ${to.join(", ")}${copied ? `, cc ${copied}` : ""}${crim ? " - crim budget, VALS to be reimbursed" : ""}${receipt ? " (claim receipt attached)" : ""}.`;
  return {
    // A crim budget invoice already counts as paid ("VALS to be reimbursed"), and its own
    // status is left as it is.
    status: crim ? item.status : item.status === "paid" ? "paid" : "finance_emailed",
    note: [String(item.note || "").trim(), line].filter(Boolean).join("\n").slice(-500),
    financeEmailedAt: at,
    financeEmailedBy: by,
    financeEmailedTo: to.join(", "),
    ...(copied ? { financeEmailedCc: copied } : {}),
    ...(receipt ? { claimReceiptName: receipt.fileName, claimReceiptUrl: receipt.fileUrl, claimReceiptPath: receipt.filePath } : {}),
  };
}

// The pages of a PDF that belong to one invoice, as a new PDF, so an invoice that came in a
// bundle of other clients' invoices is sent on its own. `pdfLib` is pdf-lib, passed in so this
// stays testable without a browser.
export async function invoicePagesOnly(bytes, page, pageCount, pdfLib) {
  if (!page) return bytes;
  const source = await pdfLib.PDFDocument.load(bytes, { ignoreEncryption: true });
  const total = source.getPageCount();
  const first = Math.min(Math.max(1, page), total) - 1;
  const last = Math.min(total, first + Math.max(1, pageCount || 1)) - 1;
  if (first === 0 && last === total - 1) return bytes;
  const out = await pdfLib.PDFDocument.create();
  const indices = Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const copied = await out.copyPages(source, indices);
  copied.forEach((p) => out.addPage(p));
  return out.save();
}

export function bytesToBase64(bytes) {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  for (let i = 0; i < view.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, view.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
